import React from 'react';
import './orthopedics-children.css';
import { Link } from "react-router-dom";

function OrthopedicsChildren() {
  const conditions = [
    {
      id: "clubfoot",
      title: "Clubfoot",
      description: "Specialized non-surgical and surgical treatments for congenital clubfoot using the Ponseti method and advanced bracing techniques.",
      icon: ""
    },
    {
      id: "hip-dysplasia",
      title: "Developmental Hip Dysplasia",
      description: "Early intervention programs including Pavlik harness, closed reduction, and hip preservation surgery for optimal joint development.",
      icon: ""
    },
    {
      id: "scoliosis",
      title: "Scoliosis & Spinal Deformities",
      description: "Comprehensive care from bracing to surgical correction for children with abnormal spinal curves.",
      icon: ""
    },
    {
      id: "limb-length",
      title: "Limb Length Discrepancy",
      description: "Advanced epiphysiodesis and limb lengthening procedures to correct unequal limb lengths.",
      icon: ""
    },
    {
      id: "cerebral-palsy",
      title: "Cerebral Palsy",
      description: "Multidisciplinary approach including Botox injections, selective dorsal rhizotomy, and orthopedic surgeries.",
      icon: ""
    },
    {
      id: "fractures",
      title: "Pediatric Fractures",
      description: "Specialized care for growing bones with minimally invasive techniques and growth-friendly fixation.",
      icon: ""
    }
  ];

  const treatments = [
    {
      id: "ponseti",
      title: "Ponseti Method",
      description: "Gentle manipulation and casting technique for clubfoot correction, followed by bracing to maintain correction."
    },
    {
      id: "growth-guidance",
      title: "Growth Guidance Surgery",
      description: "Modern techniques like guided growth for angular deformities using tension band plates."
    },
    {
      id: "spine-surgery",
      title: "Pediatric Spine Surgery",
      description: "Growth-friendly spinal implants including MAGEC rods for early-onset scoliosis."
    },
    {
      id: "hip-preservation",
      title: "Hip Preservation",
      description: "Joint-preserving surgeries like periacetabular osteotomy for adolescent hip disorders."
    }
  ];

  const teamMembers = [
    {
      id: "dr-kumara",
      name: "Dr. Asanka Kumara",
      role: "Pediatric Orthopedic Surgeon",
      qualification: "MBBS, MS Ortho, Fellowship in Pediatric Orthopedics (USA)",
      experience: "15+ Years",
      image: "/team/pediatric-surgeon1.jpg"
    },
    {
      id: "dr-perera",
      name: "Dr. Nilmini Perera",
      role: "Pediatric Physiatrist",
      qualification: "MBBS, MD Physical Medicine",
      experience: "12+ Years",
      image: "/team/pediatric-rehab1.jpg"
    },
    {
      id: "dr-fernando",
      name: "Dr. Chaminda Fernando",
      role: "Pediatric Orthotist",
      qualification: "MSc Clinical Orthotics",
      experience: "10+ Years",
      image: "/team/pediatric-orthotist1.jpg"
    }
  ];

  const whyChooseUs = [
    {
      id: "child-friendly",
      title: "Child-Friendly Environment",
      description: "Our clinic is designed to make children feel comfortable and at ease during their visits."
    },
    {
      id: "family-centered",
      title: "Family-Centered Care",
      description: "We involve parents in every decision and provide comprehensive education and support."
    },
    {
      id: "multidisciplinary",
      title: "Multidisciplinary Team",
      description: "Surgeons, therapists, and orthotists work together for comprehensive care."
    },
    {
      id: "latest-tech",
      title: "Latest Technology",
      description: "Minimally invasive techniques and growth-friendly implants for better outcomes."
    }
  ];

  return (
    <div className="pediatric-ortho-page">

      {/* Hero Section */}
      <section className="ped-hero-section">
        <div className="ped-hero-overlay">
          <div className="ped-container">
            <div className="ped-hero-content">
              <span className="ped-hero-badge">Pediatric Orthopedics</span>
              <h1 className="ped-hero-title">
                Specialized Care for <span className="ped-hero-highlight">Growing Bones</span>
              </h1>
              <p className="ped-hero-subtitle">
                Comprehensive orthopedic care for children from infancy through adolescence, 
                combining medical expertise with a gentle, child-friendly approach.
              </p>
              <div className="ped-hero-buttons">
                <Link to="/contactus" className="ped-btn ped-btn-primary">Schedule Consultation</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="ped-intro-section">
        <div className="ped-container">
          <div className="ped-intro-grid">
            <div className="ped-intro-image-wrapper">
              <img src="/orthopedics-children.png" alt="Pediatric Orthopedic Clinic" className="ped-intro-image" />
              <div className="ped-experience-badge">
                <span className="ped-experience-years">400+</span>
                <span className="ped-experience-text">Children Treated</span>
              </div>
            </div>
            <div className="ped-intro-content">
              <span className="ped-section-badge">Welcome</span>
              <h2 className="ped-intro-heading">Caring for Children's Musculoskeletal Health</h2>
              <p className="ped-intro-text">
                At Southern Rehab Care, we understand that children are not just small adults. 
                Their growing bodies require specialized knowledge, gentle techniques, and a 
                compassionate approach that puts both child and family at ease.
              </p>
              <p className="ped-intro-text">
                Our pediatric orthopedic team treats a wide range of conditions affecting the 
                developing musculoskeletal system, from common fractures to complex congenital 
                disorders. We combine advanced medical techniques with a warm, child-friendly 
                environment to ensure the best possible outcomes.
              </p>
              <div className="ped-intro-features">
                <div className="ped-intro-feature">
                  <span className="ped-intro-feature-dot"></span>
                  <span>Birth to 18 Years</span>
                </div>
                <div className="ped-intro-feature">
                  <span className="ped-intro-feature-dot"></span>
                  <span>Family-Centered Care</span>
                </div>
                <div className="ped-intro-feature">
                  <span className="ped-intro-feature-dot"></span>
                  <span>Play Therapy Available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions We Treat */}
      <section className="ped-conditions-section">
        <div className="ped-container">
          <div className="ped-section-header">
            <span className="ped-section-badge">Conditions We Treat</span>
            <h2 className="ped-section-heading">Comprehensive Pediatric Orthopedic Care</h2>
            <p className="ped-section-subheading">Specialized expertise for every childhood musculoskeletal condition</p>
          </div>
          <div className="ped-conditions-grid">
            {conditions.map((condition) => (
              <div key={condition.id} className="ped-condition-card">
                <div className="ped-condition-icon">{condition.icon}</div>
                <h3 className="ped-condition-title">{condition.title}</h3>
                <p className="ped-condition-description">{condition.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Approaches */}
      <section className="ped-treatment-section">
        <div className="ped-container">
          <div className="ped-section-header">
            <span className="ped-section-badge ped-section-badge-light">Our Approach</span>
            <h2 className="ped-section-heading ped-section-heading-light">Advanced Treatment Techniques</h2>
            <p className="ped-section-subheading ped-section-subheading-light">Minimally invasive, growth-friendly solutions</p>
          </div>
          <div className="ped-treatment-grid">
            {treatments.map((treatment) => (
              <div key={treatment.id} className="ped-treatment-card">
                <h3 className="ped-treatment-title">{treatment.title}</h3>
                <p className="ped-treatment-description">{treatment.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="ped-why-section">
        <div className="ped-container">
          <div className="ped-section-header">
            <span className="ped-section-badge">Why Families Choose Us</span>
            <h2 className="ped-section-heading">Care That Puts Children First</h2>
            <p className="ped-section-subheading">Experience the difference of specialized pediatric care</p>
          </div>
          <div className="ped-why-grid">
            {whyChooseUs.map((item) => (
              <div key={item.id} className="ped-why-card">
                <h3 className="ped-why-title">{item.title}</h3>
                <p className="ped-why-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>






      {/* Age Groups Section */}
      <section className="ped-age-section">
        <div className="ped-container">
          <div className="ped-age-grid">
            <div className="ped-age-card">
             
              <h3 className="ped-age-title">Infants (0-2 years)</h3>
              <p className="ped-age-description">
                Early intervention for congenital conditions, hip dysplasia, and birth-related musculoskeletal issues.
              </p>
            </div>
            <div className="ped-age-card">
         
              <h3 className="ped-age-title">Children (2-12 years)</h3>
              <p className="ped-age-description">
                Growing pains, fractures, gait abnormalities, and developmental conditions.
              </p>
            </div>
            <div className="ped-age-card">
          
              <h3 className="ped-age-title">Adolescents (13-18 years)</h3>
              <p className="ped-age-description">
                Sports injuries, scoliosis, and adolescent hip disorders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="ped-faq-section">
        <div className="ped-container">
          <div className="ped-section-header">
            <span className="ped-section-badge">FAQ</span>
            <h2 className="ped-section-heading">Common Questions from Parents</h2>
            <p className="ped-section-subheading">Everything you need to know about pediatric orthopedics</p>
          </div>
          <div className="ped-faq-grid">
            <div className="ped-faq-item">
              <h3 className="ped-faq-question">When should my child see a pediatric orthopedist?</h3>
              <p className="ped-faq-answer">
                If you notice persistent limping, gait abnormalities, limb deformities, or if your child complains of ongoing pain that affects their daily activities.
              </p>
            </div>
            <div className="ped-faq-item">
              <h3 className="ped-faq-question">Will my child need surgery?</h3>
              <p className="ped-faq-answer">
                Not always. Many conditions can be managed with bracing, physical therapy, or observation. Surgery is only recommended when necessary for optimal outcomes.
              </p>
            </div>
            <div className="ped-faq-item">
              <h3 className="ped-faq-question">How long is recovery after pediatric orthopedic surgery?</h3>
              <p className="ped-faq-answer">
                Recovery times vary by procedure, but children typically heal faster than adults. Most return to normal activities within 6-12 weeks.
              </p>
            </div>
            <div className="ped-faq-item">
              <h3 className="ped-faq-question">Do you accept insurance?</h3>
              <p className="ped-faq-answer">
                Yes, we work with most major insurance providers. Our staff can help verify your coverage before your first visit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ped-cta-section">
        <div className="ped-container">
          <div className="ped-cta-content">
            <h2 className="ped-cta-heading">Give Your Child the Best Possible Care</h2>
            <p className="ped-cta-subheading">Schedule a consultation with our pediatric orthopedic specialists today</p>
            <div className="ped-cta-buttons">
              <Link to="/contactus" className="ped-btn ped-btn-primary">Contact Us</Link>
              <Link to="/contactus" className="ped-btn ped-btn-secondary-light">Book Appointment</Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default OrthopedicsChildren;